import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent } from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { changePasswordRequest } from '../../store/reducers/authReducer';
import {
  toggleCurrentPasswordVisibility,
  toggleNewPasswordVisibility,
  toggleConfirmPasswordVisibility,
  setOpenPopupDialog,
} from '../../store/reducers/uiReducer';

const schema = yup.object({
  current_password: yup.string().required('Current password is required'),
  new_password: yup
    .string()
    .required('New password is required')
    .min(6, 'New password must be at least 6 characters')
    .notOneOf([yup.ref('current_password')], 'New password must be different from current password'),
  confirm_password: yup
    .string()
    .required('Confirm password is required')
    .min(6, 'Confirm password must be at least 6 characters')
    .oneOf([yup.ref('new_password'), null], 'Passwords must match')
    .notOneOf([yup.ref('current_password')], 'Confirm password must be different from current password'),
});

const PasswordChangeDialog = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector(state => state.auth);
  const {
    openPopupDialog,
    currentPasswordVisible,
    newPasswordVisible,
    confirmPasswordVisible
  } = useSelector(state => state.ui);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = (data) => {
    dispatch(changePasswordRequest({
      currentPassword: data.current_password,
      newPassword: data.new_password,
      confirmPassword: data.confirm_password,
    }));
  };

  const onError = (errors) => {
    Object.values(errors).forEach(error => {
      console.error(error.message);
    });
  };

  return (
    <Dialog
      open={openPopupDialog}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          maxWidth: "360px",
          borderRadius: '16px',
          margin: { xs: '16px', sm: 'auto' }, // mobile spacing
          width: '100%',
        },
      }}
    >
      <DialogContent className="bg-white rounded-xl p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800">
            Change Password
          </h2>
        </div>

        <div className="space-y-4">
          {/* Current Password */}
          <div className="relative">
            <label className="text-sm text-gray-700 mb-2 block">
              Current Password
            </label>
            <input
              placeholder="************"
              type={currentPasswordVisible ? "text" : "password"}
              {...register('current_password')}
              className="w-full bg-gray-300 rounded-md px-4 py-2 pr-10 text-sm sm:text-base"
            />
            {errors.current_password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.current_password.message}
              </p>
            )}

            <div className="absolute right-3 top-[36px] cursor-pointer">
              {currentPasswordVisible ? (
                <EyeOff
                  size={18}
                  onClick={() => dispatch(toggleCurrentPasswordVisibility())}
                />
              ) : (
                <Eye
                  size={18}
                  onClick={() => dispatch(toggleCurrentPasswordVisibility())}
                />
              )}
            </div>
          </div>

          {/* New Password */}
          <div className="relative">
            <label className="text-sm text-gray-700 mb-2 block">
              New Password
            </label>
            <input
              placeholder="************"
              type={newPasswordVisible ? "text" : "password"}
              {...register('new_password')}
              className="w-full bg-gray-300 rounded-md px-4 py-2 pr-10 text-sm sm:text-base"
            />
            {errors.new_password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.new_password.message}
              </p>
            )}

            <div className="absolute right-3 top-[36px] cursor-pointer">
              {newPasswordVisible ? (
                <EyeOff
                  size={18}
                  onClick={() => dispatch(toggleNewPasswordVisibility())}
                />
              ) : (
                <Eye
                  size={18}
                  onClick={() => dispatch(toggleNewPasswordVisibility())}
                />
              )}
            </div>
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <label className="text-sm text-gray-700 mb-2 block">
              Confirm Password
            </label>
            <input
              placeholder="************"
              type={confirmPasswordVisible ? "text" : "password"}
              {...register('confirm_password')}
              className="w-full bg-gray-300 rounded-md px-4 py-2 pr-10 text-sm sm:text-base"
            />
            {errors.confirm_password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.confirm_password.message}
              </p>
            )}

            <div className="absolute right-3 top-[36px] cursor-pointer">
              {confirmPasswordVisible ? (
                <EyeOff
                  size={18}
                  onClick={() => dispatch(toggleConfirmPasswordVisibility())}
                />
              ) : (
                <Eye
                  size={18}
                  onClick={() => dispatch(toggleConfirmPasswordVisibility())}
                />
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white rounded-md py-2 text-sm sm:text-base hover:bg-blue-700 disabled:opacity-50"
            onClick={handleSubmit(onSubmit, onError)}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordChangeDialog;